import { posClient } from './client';
import { organizationTypeSchema, type OrganizationType } from '@/types/auth';
import {
  wireThemeResponseSchema,
  type ConsoleThemeContext,
  type WireRawTheme,
} from '@/types/theme';

/**
 * D12 client adapter (01-architecture.md §6): produce the internal
 * { organization_type, primary_color } context from the CURRENT backend
 * responses. No new backend resource exists — the inputs are:
 * - the raw /pos/theme response (full Theme model; color_config may be null,
 *   a JSON string, or an object — pos-api casts it in neither direction), and
 * - organization objects from the login payload (type lives only on the
 *   user.organizations rows; the adapter NEVER guesses when absent).
 *
 * The fallback color (#00B8DB) is applied downstream by the ThemeStore —
 * this adapter reports null so callers can distinguish "org has no color"
 * from a real value.
 */

/** #RGB or #RRGGBB (case-insensitive). Anything else → null. */
export function validatedHex(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(trimmed) ? trimmed : null;
}

/**
 * color_config over this API: null | JSON string | object. The tenant admin
 * writes {primary, secondary, background?} (salesgroundai ThemeResource
 * ColorPickers). Returns a validated hex or null — never a fabricated color.
 */
export function extractPrimaryColor(colorConfig: unknown): string | null {
  let config: unknown = colorConfig;
  if (typeof config === 'string') {
    try {
      config = JSON.parse(config);
    } catch {
      return null;
    }
  }
  if (!config || typeof config !== 'object' || Array.isArray(config)) return null;
  return validatedHex((config as Record<string, unknown>).primary);
}

/**
 * Read organizations.type from any verified organization object(s). Accepts
 * a single candidate or a list (e.g. the selected org plus its
 * user.organizations row); the first valid restaurant|ecommerce|quotation
 * wins. Absent/unknown → null (neutral mode; the gap is recorded, not
 * papered over).
 */
export function adaptOrganizationType(candidates: unknown): OrganizationType | null {
  const list = Array.isArray(candidates) ? candidates : [candidates];
  for (const candidate of list) {
    if (!candidate || typeof candidate !== 'object') continue;
    const parsed = organizationTypeSchema.safeParse((candidate as Record<string, unknown>).type);
    if (parsed.success) return parsed.data;
  }
  return null;
}

export interface AdaptConsoleThemeInput {
  /** Parsed raw theme (or null when the fetch failed / no theme applied). */
  theme?: WireRawTheme | null;
  /** Organization object(s) from the login payload for the selected org. */
  organization?: unknown;
}

export function adaptConsoleTheme(input: AdaptConsoleThemeInput): ConsoleThemeContext {
  return {
    organization_type: adaptOrganizationType(input.organization ?? null),
    primary_color: extractPrimaryColor(input.theme?.color_config),
  };
}

export interface ThemeRequestOptions {
  signal?: AbortSignal;
}

export const themeApi = {
  /**
   * GET {pos}/theme — the applied tenant Theme model wrapped in
   * {"theme": {...}} (ThemeController.php:14-19). Tolerates a bare theme
   * object. 404 {"message":"Theme not found"} when no theme is applied —
   * callers should catch and fall back to null.
   */
  async getRaw(options: ThemeRequestOptions = {}): Promise<WireRawTheme | null> {
    const res = await posClient.get('/theme', { signal: options.signal });
    const parsed = wireThemeResponseSchema.parse(res.data);
    if (parsed && typeof parsed === 'object' && 'theme' in parsed) {
      return (parsed as { theme: WireRawTheme | null }).theme;
    }
    return parsed as WireRawTheme;
  },
};
